import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, BudgetGoal } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { ApiError, getErrorMessage, isNetworkError, getNetworkErrorMessage, type ErrorInfo } from '@/lib/errors';
import { QUERY_KEYS, QUERY_STALE_TIMES } from '@/lib/constants';
import { BUDGET_GOALS_INVALID } from '@/lib/translations';
import { invalidateBudgetSummary } from '@/hooks/queryInvalidation';

interface UseBudgetGoalsOptions {
  enabled?: boolean;
}

export function useBudgetGoals(options: UseBudgetGoalsOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [QUERY_KEYS.budgetGoals],
    queryFn: () => api.getBudgetGoals(),
    enabled,
    staleTime: QUERY_STALE_TIMES.budgetGoals,
  });
}

// The pt-BR copy for a failed goals save. A 422 means the set broke the sum-to-100 or 0–100 rules the form
// already checks (TRD §12.2), so it gets the rules restated; everything else keeps the generic copy.
export function budgetGoalsErrorInfo(error: unknown): ErrorInfo {
  if (isNetworkError(error)) return getNetworkErrorMessage();
  if (error instanceof ApiError && error.status === 422) return BUDGET_GOALS_INVALID;
  return getErrorMessage(error);
}

export function useUpdateBudgetGoals() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (goals: BudgetGoal[]) => api.updateBudgetGoals(goals),
    onSuccess: (saved) => {
      // Seed from the response, not the submitted draft: it is the canonical rendering
      // ("40" comes back as "40.00") and what the sliders should re-render from.
      queryClient.setQueryData([QUERY_KEYS.budgetGoals], saved);
      invalidateBudgetSummary(queryClient);
      toast({
        title: 'Metas salvas',
        description: 'Sua distribuição de renda foi atualizada.',
      });
    },
    onError: (error: unknown) => {
      const errorInfo = budgetGoalsErrorInfo(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}

interface UseBudgetSummaryOptions {
  from: string;
  to: string;
  enabled?: boolean;
}

export function useBudgetSummary(options: UseBudgetSummaryOptions) {
  const { from, to, enabled = true } = options;

  return useQuery({
    queryKey: [QUERY_KEYS.budgetSummary, from, to],
    queryFn: () => api.getBudgetSummary(from, to),
    enabled: enabled && !!from && !!to,
    staleTime: QUERY_STALE_TIMES.budgetSummary,
  });
}
